import { useEffect, useState } from "react";

/**
 * Custom hook to export any DOM element as a PDF using html2pdf.js
 * Usage:
 * const { exportAsPdf, isPdfReady } = usePdfExport();
 * ...
 * <button onClick={() => exportAsPdf(ref.current, 'Statement.pdf')}>Download</button>
 */
export const usePdfExport = () => {
    const [isPdfReady, setIsPdfReady] = useState(false);

    // Dynamically load html2pdf.js once
    useEffect(() => {
        const scriptId = "html2pdf-script";
        if (document.getElementById(scriptId)) {
            setIsPdfReady(true);
            return;
        }

        const script = document.createElement("script");
        script.id = scriptId;
        script.src =
            "https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js";
        script.async = true;
        script.onload = () => setIsPdfReady(true);
        document.body.appendChild(script);

        return () => {
            const existing = document.getElementById(scriptId);
            if (existing) document.body.removeChild(existing);
        };
    }, []);

    const exportAsPdf = (element, filename = "document.pdf") => {
        if (!element) {
            console.error("No element provided for PDF export.");
            return;
        }

        if (typeof window.html2pdf === "undefined") {
            alert("PDF library still loading. Please try again in a second.");
            return;
        }

        const options = {
            margin: [10, 10, 10, 10],
            filename,
            image: { type: "jpeg", quality: 0.98 },
            html2canvas: { scale: 2, useCORS: true },
            jsPDF: { unit: "mm", format: "letter", orientation: "portrait" },
            // 🔑 Page break control:
            pagebreak: {
                mode: ['avoid-all', 'css', 'legacy'],   // avoid splitting; also respect CSS rules below
                before: '.page-break-before',           // force a new page before these
                after: '.page-break-after',            // (optional) force new page after these
                avoid: ['.avoid-break', 'tr', 'td']    // try not to split these elements
            }
        };

        window.html2pdf().from(element).set(options).save();
    };

    return { exportAsPdf, isPdfReady };
};
